/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListCustomDomainMcpEndpointsRequest,
  ListCustomDomainMcpEndpointsSecurity,
} from "../models/operations/listcustomdomainmcpendpoints.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildCustomDomainMcpEndpointsQuery,
  CustomDomainMcpEndpointsQueryData,
  prefetchCustomDomainMcpEndpoints,
  queryKeyCustomDomainMcpEndpoints,
} from "./customDomainMcpEndpoints.core.js";
export {
  buildCustomDomainMcpEndpointsQuery,
  type CustomDomainMcpEndpointsQueryData,
  prefetchCustomDomainMcpEndpoints,
  queryKeyCustomDomainMcpEndpoints,
};

export type CustomDomainMcpEndpointsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMcpEndpoints domains
 *
 * @remarks
 * List the MCP endpoints registered under the organization's custom domain across every project. Returns enriched rows that include the parent MCP server and project so callers can preview what a custom-domain deletion would cascade through.
 */
export function useCustomDomainMcpEndpoints(
  request?: ListCustomDomainMcpEndpointsRequest | undefined,
  security?: ListCustomDomainMcpEndpointsSecurity | undefined,
  options?: QueryHookOptions<
    CustomDomainMcpEndpointsQueryData,
    CustomDomainMcpEndpointsQueryError
  >,
): UseQueryResult<
  CustomDomainMcpEndpointsQueryData,
  CustomDomainMcpEndpointsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildCustomDomainMcpEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpEndpoints domains
 *
 * @remarks
 * List the MCP endpoints registered under the organization's custom domain across every project. Returns enriched rows that include the parent MCP server and project so callers can preview what a custom-domain deletion would cascade through.
 */
export function useCustomDomainMcpEndpointsSuspense(
  request?: ListCustomDomainMcpEndpointsRequest | undefined,
  security?: ListCustomDomainMcpEndpointsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    CustomDomainMcpEndpointsQueryData,
    CustomDomainMcpEndpointsQueryError
  >,
): UseSuspenseQueryResult<
  CustomDomainMcpEndpointsQueryData,
  CustomDomainMcpEndpointsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildCustomDomainMcpEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setCustomDomainMcpEndpointsData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: CustomDomainMcpEndpointsQueryData,
): CustomDomainMcpEndpointsQueryData | undefined {
  const key = queryKeyCustomDomainMcpEndpoints(...queryKeyBase);

  return client.setQueryData<CustomDomainMcpEndpointsQueryData>(key, data);
}

export function invalidateCustomDomainMcpEndpoints(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "domains", "listMcpEndpoints", ...queryKeyBase],
  });
}

export function invalidateAllCustomDomainMcpEndpoints(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "domains", "listMcpEndpoints"],
  });
}
